import { ACCOUNT_API_COMPANIES, SUBSCRIPTIONS_API_ROUTE } from 'apiRoutes';
import axios from 'axios';
import { Country } from 'domain/account/interfaces';
import { SubscribableApplication } from 'domain/subscription/interfaces/SubscribableApplication';
import {
	ApplicationPermissionOptions,
	GeoNode,
	PermissionSet,
	SubscriptionPlan,
} from 'domain/subscription/interfaces/Subscription';
import { ApplicationPermissions } from 'pages/Subscription/SubscriptionForm/SubscriptionFormContext';
import {
	FunctionComponent,
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';
import { collapseCompleteBranchesToParents } from 'utils/geoNodeHelpers';

export type GlobalContextType = {
	permissionOptions: ApplicationPermissionOptions[];
	permissionOptionsLoaded: boolean;
	countries: Country[];
	getPlanByCode: (applicationId: string, planCode: string) => any;
	getAddonByCode: (applicationId: string, addonCode: string) => any;
	getAreaByCode: (applicationId: string, areaCode: string) => any;
	getSubscribableApplicationById: (
		applicationId: string,
	) => SubscribableApplication;
	toApplicationPermissions: (
		permissions: Record<string, PermissionSet>,
	) => ApplicationPermissions[];
	getMinimalAreaRepresentation: (
		applicationId: string,
		countryCodes: string[],
	) => GeoNode[];
};

export const GlobalContext = createContext<GlobalContextType | null>(null);

const GlobalContextProvider: FunctionComponent<{
	children: ReactNode;
}> = ({ children }) => {
	const [permissionOptions, setPermissionOptions] = useState<
		ApplicationPermissionOptions[]
	>([]);
	const [permissionOptionsLoaded, setPermissionOptionsLoaded] =
		useState(false);

	const getAddonByCode = (applicationId: string, addonCode: string) => {
		const app = permissionOptions.find(
			(app) => app.applicationId === applicationId,
		);

		const appAddon = app?.addons?.find((addon) => addon.code === addonCode);

		return appAddon;
	};

	const getPlanByCode = (
		applicationId: string,
		planCode: string,
	): SubscriptionPlan => {
		const app = permissionOptions.find(
			(app) => app.applicationId === applicationId,
		);
		return (
			app?.plans.find((plan) => plan.code === planCode) ?? {
				applicationId: applicationId,
				code: planCode,
				displayName: planCode + ' (invalid)',
			}
		);
	};

	const getAreaByCode = (applicationId: string, areaCode: string) => {
		const app = permissionOptions.find(
			(app) => app.applicationId === applicationId,
		);

		const recursiveSearch = (code: string, node: GeoNode): any => {
			if (node.code === code) {
				return node;
			}

			if (node.children) {
				for (const child of node.children) {
					const result = recursiveSearch(code, child);
					if (result) {
						return result;
					}
				}
			}

			return null;
		};

		if (app?.areas) {
			for (const area of app.areas) {
				const result = recursiveSearch(areaCode, area);
				if (result) {
					return result;
				}
			}
			return {
				code: areaCode,
				name: areaCode + ' (invalid)',
			};
		}
	};

	const getSubscribableApplicationById = (
		applicationId: string,
	): SubscribableApplication => {
		const app = permissionOptions.find(
			(app) => app.applicationId === applicationId,
		) as ApplicationPermissionOptions;

		return {
			id: app?.applicationId,
			name: app?.applicationName,
			eligibleSubscriber: app?.subscriberType,
		};
	};

	const getMinimalAreaRepresentation = (
		applicationId: string,
		countryCodes: string[],
	): GeoNode[] => {
		const app = permissionOptions.find(
			(app) => app.applicationId === applicationId,
		);

		if (!app?.areas) {
			return [];
		}

		return collapseCompleteBranchesToParents(app.areas, countryCodes);
	};

	const toApplicationPermissions = (
		permissions: Record<string, PermissionSet>,
	): ApplicationPermissions[] => {
		const map = new Map(Object.entries(permissions));
		const applicationPermissions: ApplicationPermissions[] = [];

		map.forEach((set, applicationId) => {
			applicationPermissions.push({
				application: getSubscribableApplicationById(applicationId),
				plan: getPlanByCode(applicationId, set.plan) as any,
				addons: set.addons.map(
					(addonCode) =>
						getAddonByCode(applicationId, addonCode) as any,
				),
				countries: set.countries.map(
					(areaCode) => getAreaByCode(applicationId, areaCode) as any,
				),
			});
		});

		return applicationPermissions;
	};

	const [countries, setCountries] = useState<Country[]>([]);

	useEffect(() => {
		if (permissionOptions.length) return;

		async function getPermissionOptions() {
			const response = await axios.get(
				`${SUBSCRIPTIONS_API_ROUTE}/GetPermissionOptions`,
			);

			setPermissionOptions(response.data);
			setPermissionOptionsLoaded(true);
		}

		getPermissionOptions();
	}, []);

	useEffect(() => {
		if (countries.length) return;

		async function getCountries() {
			const response = await axios.get(
				`${ACCOUNT_API_COMPANIES}/GetCountries`,
			);

			setCountries(response.data);
		}

		getCountries();
	}, []);

	return (
		<GlobalContext.Provider
			value={{
				permissionOptions,
				permissionOptionsLoaded,
				countries,
				getPlanByCode,
				getAddonByCode,
				getAreaByCode,
				getSubscribableApplicationById,
				toApplicationPermissions,
				getMinimalAreaRepresentation,
			}}
		>
			{children}
		</GlobalContext.Provider>
	);
};

export function useGlobalContext() {
	const context = useContext(GlobalContext);
	if (!context) {
		throw new Error(
			'useGlobalContext must be used within the GlobalContextProvider',
		);
	}
	return context;
}

export default GlobalContextProvider;
